import React from "react";
import { Activity, ArrowRight, RefreshCw } from "lucide-react";

import { Badge } from "@/components/primitives/Badge";
import { Button } from "@/components/primitives/Button";
import { Card } from "@/components/primitives/Card";
import { Header } from "@/components/layout/Header";
import { PageShell } from "@/components/layout/PageShell";
import { useServiceStatus } from "@/hooks/useServiceStatus";

const formatUptime = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return "—";
  }
  return `${value.toFixed(2)}%`;
};

export const ServiceStatusPage: React.FC = () => {
  const { items, isLoading, error, refresh } = useServiceStatus();

  return (
    <PageShell>
      <Header
        action={
          <Button
            variant="secondary"
            onClick={() => (window.location.href = "/flows")}
            className="hidden sm:inline-flex"
          >
            Manage flows
          </Button>
        }
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white pb-12 pt-10 dark:bg-slate-950">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -right-20 top-0 h-96 w-96 rounded-full bg-softnu-300/30 blur-3xl dark:bg-softnu-500/20" />
          <div className="absolute -left-20 top-20 h-72 w-72 rounded-full bg-purple-300/20 blur-3xl dark:bg-purple-500/10" />
        </div>

        <div className="page-container relative">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-softnu-100 bg-softnu-50 px-4 py-2 text-sm font-medium text-softnu-700 dark:border-softnu-800 dark:bg-softnu-900/30 dark:text-softnu-300">
              <Activity className="h-4 w-4" />
              <span>Live system health</span>
            </div>
            <h1 className="mb-4 text-4xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-5xl">
              Service Status
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-300">
              Real-time health overview for every monitored flow.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <main className="page-container pb-16">
        <div className="mb-6 flex items-center justify-between">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {isLoading ? "Loading status…" : `${items.length} flow${items.length !== 1 ? "s" : ""} monitored`}
          </p>
          <Button variant="ghost" size="sm" onClick={refresh} disabled={isLoading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {error && (
          <div
            className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            role="alert"
          >
            {error}
          </div>
        )}

        {!isLoading && items.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 py-20 text-center dark:border-slate-700 dark:bg-slate-900/30">
            <h3 className="mb-2 text-xl font-semibold text-slate-900 dark:text-white">
              No flows configured
            </h3>
            <p className="mb-6 max-w-sm text-slate-500 dark:text-slate-400">
              Create a flow to start monitoring a service.
            </p>
            <Button onClick={() => (window.location.href = "/flows")}>Create your first flow</Button>
          </div>
        )}

        {items.length > 0 && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map(({ flow, stats24h, stats7d, stats30d, error: itemError }) => {
              const lastStatus = stats24h?.last_run_status;
              const badgeVariant =
                !flow.is_active
                  ? "neutral"
                  : lastStatus === "success"
                    ? "success"
                    : lastStatus === "failure"
                      ? "danger"
                      : "neutral";

              const badgeLabel = !flow.is_active ? "Paused" : lastStatus ?? "Unknown";

              return (
                <Card
                  key={flow.id}
                  className="group cursor-pointer p-0 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
                  onClick={() => (window.location.href = `/dashboard/${flow.id}`)}
                >
                  <div className="p-6">
                    <div className="mb-4 flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                          {flow.name}
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {flow.steps.length} step{flow.steps.length !== 1 ? "s" : ""} ·{" "}
                          {flow.is_active ? "Active" : "Paused"}
                        </p>
                      </div>
                      <Badge variant={badgeVariant}>{badgeLabel}</Badge>
                    </div>

                    {itemError ? (
                      <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                        {itemError}
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 gap-3">
                        <div className="rounded-xl bg-slate-50 p-3 text-center dark:bg-slate-800/50">
                          <span className="block text-xs font-medium text-slate-500 dark:text-slate-400">
                            24h
                          </span>
                          <span className="block text-lg font-semibold text-slate-900 dark:text-white">
                            {formatUptime(stats24h?.uptime_percentage)}
                          </span>
                        </div>
                        <div className="rounded-xl bg-slate-50 p-3 text-center dark:bg-slate-800/50">
                          <span className="block text-xs font-medium text-slate-500 dark:text-slate-400">
                            7d
                          </span>
                          <span className="block text-lg font-semibold text-slate-900 dark:text-white">
                            {formatUptime(stats7d?.uptime_percentage)}
                          </span>
                        </div>
                        <div className="rounded-xl bg-slate-50 p-3 text-center dark:bg-slate-800/50">
                          <span className="block text-xs font-medium text-slate-500 dark:text-slate-400">
                            30d
                          </span>
                          <span className="block text-lg font-semibold text-slate-900 dark:text-white">
                            {formatUptime(stats30d?.uptime_percentage)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between border-t border-slate-100 px-6 py-3 text-sm text-softnu-600 dark:border-slate-800 dark:text-softnu-400">
                    <span>View details</span>
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </PageShell>
  );
};
